// Opening an artifact in the right-side viewer pane (#11523), shared by the
// library, the record and agent tabs, chat chips and — since #11819 — a chat
// citation that names an artifact. One builder for the pane detail, so every
// opener presents the same artifact the same way.
import {
  artifactUrls,
  canEditArtifact,
  type ArtifactItem,
} from "./useArtifacts";
import type { TableCellLink } from "../../FileViewer/tableCellLink";

const isTextFile = (filename: string): boolean =>
  /\.(md|markdown|txt)$/i.test(filename);

/** The pane-detail shape one artifact opens as. A shared (not-mine)
 *  artifact opens VIEW-ONLY (r47): without an artifactId the panes offer no
 *  hand-edit or restore, whose writes are creator-only server-side anyway. */
export const paneDetailFor = (
  artifact: ArtifactItem,
  citedValues?: readonly string[],
  // A deep link to one cell's source (#13788): the table lands on the cell
  // and opens that source.
  cellLink?: TableCellLink
): Record<string, unknown> => {
  if (artifact.workspace_id) {
    // A workspace-backed Document opens in the SAME Layout pane as every
    // other kind (#11548 r51, r59). The editor body's read-only-ness is
    // enforced by the workspace's own canUpdate permission, so no
    // editable flag rides here.
    return {
      fileName: artifact.title,
      editorDocument: {
        workspaceId: artifact.workspace_id,
        title: artifact.title,
        artifactId: artifact.id,
      },
    };
  }
  // r60b: identity and editability travel separately. Every branch sends
  // the id — the pane's action cluster (Share, Versions, Assign, Copy
  // link, Chat) renders off it for shared artifacts too — while
  // the hand-edit affordance alone follows the edit capability: the
  // creator, or an Editor of the producing agent (#13422).
  const editable = canEditArtifact(artifact);
  const urls = artifactUrls(artifact.id);
  if (artifact.kind === "diagram") {
    return {
      fileName: artifact.title,
      diagram: artifact.content ?? "",
      artifactId: artifact.id,
      artifactEditable: editable,
    };
  }
  if (artifact.kind === "table") {
    // A table (#11727): its JSON rides inline like a diagram's Mermaid
    // source; the pane renders and hand-edits it. A citation's open carries
    // what it cited, so the pane lands on the cell (#12818).
    return {
      tableArtifact: {
        artifactId: artifact.id,
        editable,
        title: artifact.title,
        content: artifact.content ?? "",
        ...(citedValues && citedValues.length > 0 ? { citedValues } : {}),
        ...(cellLink ? { cellLink } : {}),
      },
    };
  }
  if (artifact.content !== null && !artifact.filename) {
    // A content-backed document (#11548 r14b): inline markdown, no file
    // behind it. The download route serves the content, so the text viewer
    // reads through it like any produced text file.
    return {
      fileName: `${artifact.title}.md`,
      textArtifact: {
        artifactId: artifact.id,
        editable,
        title: artifact.title,
        filename: `${artifact.title}.md`,
        downloadUrl: urls.downloadUrl,
      },
    };
  }
  if (artifact.filename && isTextFile(artifact.filename)) {
    return {
      fileName: artifact.filename,
      textArtifact: {
        artifactId: artifact.id,
        editable,
        title: artifact.title,
        filename: artifact.filename,
        downloadUrl: urls.downloadUrl,
      },
    };
  }
  return {
    fileName: artifact.filename ?? artifact.title,
    artifactId: artifact.id,
    artifactEditable: editable,
    ...urls,
  };
};

/** Open a produced-file, text, diagram, or table artifact in the right-side
 *  document viewer — the same pane chat outputs and citations open into. A
 *  diagram carries its Mermaid source inline, a table its JSON; a
 *  markdown/text file opens the hand-editable text pane; every other file
 *  is handed its preview URLs.
 *  artifactId rides along so the pane can write hand-edits back (#11523),
 *  and a citation's `citedValues` so a table can mark the cited cell
 *  (#12818). */
export const openArtifact = (
  artifact: ArtifactItem,
  citedValues?: readonly string[],
  // A chat hand-off's open (#13261): the funnel keeps the side-by-side
  // presentation it set for it rather than ending it.
  options: { besideChat?: boolean; cellLink?: TableCellLink } = {}
): void => {
  // Every artifact kind honors the ARTIFACT viewer mode — its own memory,
  // split by default, independent of any surface's document choice (Gerry,
  // 2026-09-01). The routing lives in Layout's openDocumentPanel funnel,
  // which hands artifact details to the overlay drawer in overlay mode —
  // one decision point for every opener, this one included.
  window.dispatchEvent(
    new CustomEvent("openDocumentPanel", {
      detail: {
        ...paneDetailFor(artifact, citedValues, options.cellLink),
        ...(options.besideChat ? { besideChat: true } : {}),
      },
    })
  );
};
